import { useState, useEffect, useMemo } from "react";
import { Search, Download, Mail, Users, Sun, Send, X } from "lucide-react";
import { getWaitlist, exportWaitlist, sendWaitlistEmail } from "../utils/api";
import RichTextEditor from "../components/RichTextEditor";

const UNSUBSCRIBE_TEXT =
  "You are receiving this because you joined the waitlist at thehealthhire.com. To unsubscribe, reply to this email.";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
}


export default function Waitlist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // bulk email state
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    getWaitlist()
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load waitlist"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    const q = searchInput.trim().toLowerCase();
    if (!q) return data.entries;
    return data.entries.filter(
      (e) => (e.name || "").toLowerCase().includes(q) || (e.email || "").toLowerCase().includes(q)
    );
  }, [data, searchInput]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const res = await exportWaitlist();
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `waitlist_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Export failed. Please try again.");
    } finally { setExporting(false); }
  };

  const handleSend = async () => {
    setShowConfirm(false);
    setSending(true);
    setSendError("");
    setSendResult(null);
    try {
      const res = await sendWaitlistEmail({
        subject: subject.trim(),
        body_html: body,
      });
      setSendResult(res.data);
      setSubject("");
      setBody("");
    } catch (err) {
      setSendError(err.response?.data?.error || "Failed to send emails");
    } finally { setSending(false); }
  };

  const canSend = subject.trim() && body && (data?.total ?? 0) > 0;

  return (
    <div>
      <h1 className="page-title">Waitlist</h1>
      <p className="page-sub">Pre-launch signups from the coming-soon page.</p>

      {error && <div className="error-box" style={{ marginTop: 16 }}>{error}</div>}

      {/* Stats */}
      <div className="stat-grid" style={{ marginTop: 20, gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
        <div className="card" style={{ padding: 18, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: "#e8f1fd",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <Users size={20} style={{ color: "#1976e0" }} />
          </div>
          <div>
            <p style={{ fontSize: 24, fontWeight: 800 }}>{data?.total ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#718096", fontWeight: 600, marginTop: 3 }}>Total Signups</p>
          </div>
        </div>
        <div className="card" style={{ padding: 18, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: "#fffbeb",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <Sun size={20} style={{ color: "#d97706" }} />
          </div>
          <div>
            <p style={{ fontSize: 24, fontWeight: 800 }}>{data?.today ?? "—"}</p>
            <p style={{ fontSize: 12, color: "#718096", fontWeight: 600, marginTop: 3 }}>Joined Today</p>
          </div>
        </div>
      </div>

      {/* Search + export */}
      <div style={{
        display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
        margin: "20px 0 14px", justifyContent: "space-between"
      }}>
        <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 360 }}>
          <Search size={15} style={{
            position: "absolute", left: 13, top: "50%",
            transform: "translateY(-50%)", color: "#a0aec0"
          }} />
          <input
            className="input" placeholder="Search by name or email…"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </div>
        <button className="btn btn-ghost" onClick={handleExport} disabled={exporting || !data?.total}>
          <Download size={15} /> {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {/* Table */}
      <div className="card table-wrap">
        {loading ? (
          <div className="empty"><span className="spinner spinner-dark" style={{ width: 26, height: 26 }} /></div>
        ) : !filtered.length ? (
          <div className="empty">
            {searchInput ? `No matches for “${searchInput}”.` : "No waitlist signups yet."}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.email}>
                  <td style={{ fontWeight: 600 }}>{e.name}</td>
                  <td style={{ color: "#718096" }}>{e.email}</td>
                  <td style={{ color: "#718096", whiteSpace: "nowrap" }}>{fmtDate(e.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Bulk email ── */}
      <div className="card" style={{ marginTop: 28, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Mail size={18} style={{ color: "#1976e0" }} />
          <h2 style={{ fontSize: 17, fontWeight: 800 }}>Email the Waitlist</h2>
        </div>
        <p style={{ fontSize: 13, color: "#718096", marginBottom: 18 }}>
          Sends an individual email to every waitlist member via SendGrid. An unsubscribe
          footer is added automatically.
        </p>

        {sendResult && (
          <div className="success-box" style={{ marginBottom: 14 }}>
            Done — {sendResult.sent} sent, {sendResult.failed} failed
            (of {sendResult.total} members).
          </div>
        )}
        {sendError && <div className="error-box" style={{ marginBottom: 14 }}>{sendError}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Subject
          </label>
          <input className="input" placeholder="We're launching soon 🚀"
            value={subject} onChange={(e) => setSubject(e.target.value)} disabled={sending} />
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>
            Message
          </label>
          <RichTextEditor
            value={body}
            onChange={setBody}
            disabled={sending}
            placeholder="Hi there,&#10;&#10;We have exciting news to share…"
          />
        </div>

        {/* Preview */}
        {(subject.trim() || body) && (
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, display: "block", marginBottom: 6 }}>
              Preview
            </label>
            <div style={{ border: "1px solid #dfe3e8", borderRadius: 12, overflow: "hidden" }}>
              <div style={{
                background: "#f4f6f8", padding: "10px 16px",
                borderBottom: "1px solid #dfe3e8", fontSize: 13
              }}>
                <strong>Subject:</strong> {subject || <em style={{ color: "#a0aec0" }}>(no subject)</em>}
              </div>
              <div style={{ background: "#fff" }}>
                <div style={{ padding: "12px 24px", fontFamily: "Arial, Helvetica, sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: body }} />
                <div style={{
                  borderTop: "1px solid #dfe3e8", margin: "0 24px", paddingTop: 14, paddingBottom: 12
                }}>
                  <p style={{ color: "#a0aec0", fontSize: 12, lineHeight: 1.6 }}>{UNSUBSCRIBE_TEXT}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <button className="btn btn-primary" disabled={!canSend || sending}
          onClick={() => setShowConfirm(true)}>
          {sending
            ? <><span className="spinner" /> Sending…</>
            : <><Send size={15} /> Send to all {data?.total ?? 0} members</>}
        </button>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <h3 style={{ fontSize: 17, fontWeight: 800 }}>Send bulk email?</h3>
              <button onClick={() => setShowConfirm(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ fontSize: 13.5, color: "#718096", margin: "12px 0 4px", lineHeight: 1.7 }}>
              This will send <strong style={{ color: "#1a1f2e" }}>“{subject}”</strong> to{" "}
              <strong style={{ color: "#1a1f2e" }}>{data?.total ?? 0} people</strong> on the waitlist.
              This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSend}>
                <Send size={14} /> Yes, send it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
